import { useCallback, useEffect, useMemo, useState } from "react"
import { UsersRound } from "lucide-react"
import { fetchMentorBreakoutPlan, type MentorBreakoutPlan } from "services/api/meetingsClient"
import type { BreakoutParticipant } from "./BreakoutPanel"

export type GeneratedMentorRooms = { names: string[]; rooms: string[][]; mentors: string[]; version: string }

export function generateMentorRooms(plan: MentorBreakoutPlan, participants: BreakoutParticipant[], includeLocalHost: boolean) {
  const present = new Map(participants.filter((person) => person.identityKey).map((person) => [person.identityKey, person]))
  const names: string[] = []
  const rooms: string[][] = []
  const mentors: string[] = []
  const notices: string[] = []
  for (const group of plan.groups) {
    const mentor = present.get(`user:${group.mentor.id}`)
    const mentees = group.mentees.map((person) => present.get(`user:${person.id}`)).filter((person): person is BreakoutParticipant => Boolean(person))
    if (!mentor) notices.push(`${group.mentor.name} is not here. Their mentees stay in the main room.`)
    else if (mentor.isOwner && mentor.isLocal && !includeLocalHost) notices.push(`You are assigned as a mentor. Choose to include yourself or keep your mentees in the main room.`)
    else if (!mentees.length) notices.push(`${group.mentor.name}: no assigned mentees are here yet.`)
    else {
      names.push(`${group.mentor.name}'s room`)
      rooms.push([mentor.sessionId, ...mentees.map((person) => person.sessionId)])
      mentors.push(mentor.sessionId)
    }
    const missing = group.mentees.filter((person) => !present.has(`user:${person.id}`))
    if (missing.length) notices.push(`${group.mentor.name}: ${missing.map((person) => person.name).join(", ")} not here yet.`)
  }
  return { names, rooms, mentors, version: plan.version, notices }
}

export function MentorAssignmentPicker({ meetingId, participants, applied, onApply }: {
  meetingId: string
  participants: BreakoutParticipant[]
  applied: boolean
  onApply: (plan: GeneratedMentorRooms) => void
}) {
  const [plan, setPlan] = useState<MentorBreakoutPlan | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [includeHost, setIncludeHost] = useState(false)
  const load = useCallback(async () => {
    setLoading(true)
    setError("")
    try { const next = await fetchMentorBreakoutPlan(meetingId); setPlan(next); return next }
    catch { setError("Mentor assignments could not be loaded. You can retry or use ordinary room setup."); return null }
    finally { setLoading(false) }
  }, [meetingId])
  useEffect(() => { void load() }, [load])
  const hostIsMentor = Boolean(plan?.groups.some((group) => participants.some((person) => person.isOwner && person.isLocal && person.identityKey === `user:${group.mentor.id}`)))
  const generated = useMemo(() => plan ? generateMentorRooms(plan, participants, includeHost) : null, [includeHost, participants, plan])

  const apply = async () => {
    const latest = await load()
    if (!latest) return
    onApply(generateMentorRooms(latest, participants, includeHost))
  }

  if (!error && !loading && !plan?.groups.length) return null
  return (
    <section aria-label="Mentor assignments" className="space-y-2 rounded-xl border border-white/15 bg-white/5 p-3">
      <button type="button" disabled={loading || !plan?.groups.length} onClick={() => void apply()} className="flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-[#f6a35c] px-3 text-sm font-semibold text-[#1c2530] disabled:opacity-50">
        <UsersRound className="h-4 w-4" />{loading ? "Loading mentor assignments…" : applied ? "Refresh mentor assignments" : "Use mentor assignments"}
      </button>
      {hostIsMentor && <label className="flex items-start gap-2 text-xs text-white/80"><input type="checkbox" checked={includeHost} onChange={(event) => { const include = event.target.checked; setIncludeHost(include); if (plan && applied) onApply(generateMentorRooms(plan, participants, include)) }} className="mt-0.5 h-4 w-4 accent-[#f6a35c]" />Include me in my mentor room. I can return to the main room.</label>}
      {applied && <p className="text-xs text-white/65">Review before starting. Unpaired participants and unresolved groups stay in the main room. Refresh the plan to include late arrivals.</p>}
      {applied && generated?.notices.map((notice) => <p key={notice} className="text-xs text-[#f6cf93]">{notice}</p>)}
      {error && <p role="alert" className="text-xs text-red-200">{error} <button type="button" onClick={() => void load()} className="underline">Retry</button></p>}
    </section>
  )
}
